import React, { useEffect, useMemo, useState } from "react";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RootState, AppDispatch } from "../../../../redux/store";
import {
  listenToCleaningGuestReservations,
} from "../../../../redux/cleaningReservationSlice";
import { listenToMultiCleanings } from "../../../../redux/cleaningSlice";
import dayjs from "dayjs";

/* ── green theme tokens ── */
const G = {
  green:       "#16a34a",
  greenDark:   "#14532d",
  greenLight:  "#dcfce7",
  greenBorder: "#bbf7d0",
  greenBorder2:"#86efac",
  red:         "#C0392B",
  redLight:    "#FDEEEC",
  muted:       "#6b7280",
  text:        "#14532d",
  textSoft:    "#166534",
  surface:     "#ffffff",
  bg:          "#f0fdf4",
  sans:        "'Montserrat', sans-serif",
  serif:       "'Cormorant Garamond', serif",
};

/* ════════════════════════════════════════════════════════ */
export default function CleaningBookings() {
  const [open, setOpen] = useState<{ [id: string]: boolean }>({});

  const { userId } = useParams<{ userId: string }>();
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();

  /* ── selectors ── */
  const reservations = useSelector((s: RootState) => s.cleaningReservations.guestReservations);
  const listings     = useSelector((s: RootState) => s.cleaning.listings);
  const currentUser  = useSelector((s: RootState) => s.auth.user);
  const authLoaded   = useSelector((s: RootState) => s.auth.authLoaded);

  /* ── auth guard ── */
  useEffect(() => {
    if (!authLoaded) return;
    if (!currentUser) { navigate("/AuthSwitch", { replace: true }); return; }
    if (userId && currentUser.userId !== userId) navigate("/", { replace: true });
  }, [authLoaded, currentUser, userId, navigate]);

  /* ── load reservations ── */
  useEffect(() => {
    if (authLoaded && userId) dispatch(listenToCleaningGuestReservations(userId));
  }, [dispatch, authLoaded, userId]);

  /* ── load missing listings ── */
  useEffect(() => {
    const fetchedIds = listings.map((l) => l.listingId);
    const missingIds = reservations
      .map((r) => r.listingId)
      .filter((id) => !fetchedIds.includes(id));
    if (!missingIds.length) return;
    const chunk = (arr: string[], size = 10) =>
      Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
        arr.slice(i * size, i * size + size)
      );
    chunk(missingIds).forEach((ids) => dispatch(listenToMultiCleanings(ids)));
  }, [reservations, listings, dispatch]);

  /* ── listings map ── */
  const listingsMap = useMemo(() => {
    const m: Record<string, any> = {};
    listings.forEach((l) => { m[l.listingId] = l; });
    return m;
  }, [listings]);

  /* ── active reservations ── */
  const activeReservations = useMemo(
    () =>
      reservations
        .filter((r) => !r.isCancelled && !r.isCompleted)
        .sort((a, b) =>
          (a.checkIn ? new Date(a.checkIn).getTime() : 0) -
          (b.checkIn ? new Date(b.checkIn).getTime() : 0)
        ),
    [reservations]
  );

  /* ── helpers ── */
  const openGoogleMaps = (lat: any, lng: any) =>
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");

  const formatAddress = (addr: any) => {
    if (!addr) return "";
    return [addr.street, addr.apt ? `Apt ${addr.apt}` : "", addr.city, addr.county, addr.zip, addr.country]
      .filter(Boolean).join(", ");
  };

  const renderStatuses = (res: any) => {
    if (!res) return null;
    const flags: Record<string, string> = {
      isArrivingSoon: "Arriving Soon",
      isUpcoming:     "Upcoming",
      isOngoing:      "Ongoing",
      isCheckingIn:   "Service Starting",
      isCheckingOut:  "Service Ending",
    };
    const active = Object.entries(flags).find(([key]) => res[key] === true);
    if (!active) return null;
    const [key, label] = active;
    const isActive = key === "isOngoing" || key === "isCheckingIn";
    return (
      <div style={{
        marginBottom: 12, padding: "10px 14px", borderRadius: 10,
        fontSize: 13, fontWeight: 800, textAlign: "center",
        background: isActive ? G.greenLight : "#fff4e5",
        color:      isActive ? G.greenDark  : "#b45309",
        border: `1px solid ${isActive ? G.greenBorder2 : "#f5d0a6"}`,
      }}>
        {label}
      </div>
    );
  };

  /* ════════════════════ RENDER ════════════════════ */
  return (
    <div style={s.centerWrap}>
      {activeReservations.map((reservation) => {
        const listing = listingsMap[reservation.listingId];

        const prettyCheckIn  = reservation.checkIn  ? dayjs(reservation.checkIn).format("DD MMM YYYY")  : "—";
        const prettyCheckOut = reservation.checkOut ? dayjs(reservation.checkOut).format("DD MMM YYYY") : "—";

        const isPerHour = reservation.rateType === "perHour" || reservation.rateType === "perHourPerPerson";
        const totalHours = reservation.totalHours ?? 0;

        const durationValue = isPerHour
          ? (reservation.totalHours || 0)
          : (reservation.reservedDuration || reservation.reservedDates || 0);
        const unitLabel = isPerHour ? "hour" : "day";

        const unitRate   = listing?.rate || 0;
        const serviceFee = reservation.guestsFee || 0;
        const totalPaid  = reservation.totalPaid  || 0;

        const now             = dayjs();
        const checkInDateTime = reservation.checkIn ? dayjs(reservation.checkIn) : null;
        const checkInDate     = reservation.checkIn ? dayjs(reservation.checkIn).startOf("day") : null;
        const today           = dayjs().startOf("day");

        const isWithin24Hours = !!(
          checkInDateTime &&
          checkInDateTime.diff(now, "hour") <= 24 &&
          checkInDateTime.isAfter(now)
        );

        const isCheckInTodayOrPast = !!(
          checkInDate && (checkInDate.isSame(today) || checkInDate.isBefore(today))
        );

        const disableAccess = !isWithin24Hours;

        const isOpen = open[reservation.id] ?? false;

        /* duration label */
        const durationLabel = (() => {
          const rt = listing?.rateType ?? reservation.rateType;
          if (rt === "perHour")  return `${totalHours}h service`;
          if (rt === "perWeek")  return `${Math.ceil(durationValue / 7)} week(s)`;
          if (rt === "perMonth") return `${Math.ceil(durationValue / 30)} month(s)`;
          return `${durationValue} day${durationValue !== 1 ? "s" : ""}`;
        })();

        return (
          <aside key={reservation.id} style={s.stickyCard}>

            {/* 🧹 LISTING SUMMARY */}
            <section style={s.listingCard}>
              <div style={s.listingLeft}>
                <strong style={s.listingTitle}>{listing?.name || listing?.title || "Cleaning Service"}</strong>

                {listing?.address && (
                  <div
                    style={{ ...s.listingAddress, textDecoration: "underline", cursor: listing.address.latitude ? "pointer" : "default" }}
                    onClick={() => listing.address?.latitude && openGoogleMaps(listing.address.latitude, listing.address.longitude)}
                  >
                    {formatAddress(listing.address) || "View on map"}
                  </div>
                )}

                {listing && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 4, background: G.greenLight, borderRadius: 999, padding: "3px 8px", width: "fit-content" }}>
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: G.green }} />
                    <span style={{ color: G.green, fontSize: 10, fontWeight: 700 }}>
                      {listing.isCompany ? "Professional Company" : "Individual Cleaner"}
                    </span>
                  </div>
                )}
              </div>

              <div style={s.listingImageWrap}>
                {listing?.photos?.[0]
                  ? <img src={listing.photos[0]} alt="Listing" style={s.listingImage} />
                  : <div style={s.imagePlaceholder} />
                }
              </div>
            </section>

            {/* 📅 DATES CARD */}
            <div style={s.inputCard}>
              <div style={s.grid2}>
                <div style={s.inputCell}>
                  <div style={s.inputLabel}>SERVICE DATE</div>
                  <div style={s.inputValue}>{prettyCheckIn}</div>
                </div>
                <div style={{ ...s.inputCell, borderLeft: `1px solid ${G.greenBorder}` }}>
                  <div style={s.inputLabel}>{isPerHour ? "DATE" : "END DATE"}</div>
                  <div style={s.inputValue}>{isPerHour ? "Same day" : prettyCheckOut}</div>
                </div>
              </div>
              <div style={s.guestRow}>
                <div style={s.inputLabel}>{isPerHour ? "HOURS" : "DURATION"}</div>
                <div style={s.inputValue}>
                  {isPerHour
                    ? `${totalHours} hour${totalHours !== 1 ? "s" : ""}`
                    : durationLabel
                  }
                </div>
              </div>
            </div>

            <div style={{ flex: 1 }} />

            {/* 🚀 ACTIONS CARD */}
            <div style={s.actionsCard}>

              {/* ROW 1 */}
              <div style={s.row2}>
                <button
                  style={{
                    ...s.actionBtn,
                    ...(isCheckInTodayOrPast ? s.disabledBtn : {}),
                  }}
                  disabled={isCheckInTodayOrPast}
                  onClick={() =>
                    navigate(`/cancelcleaningbooking/${userId}/${reservation.listingId}/${reservation.id}`)
                  }
                >
                  Cancel Booking
                </button>

                <button
                  style={{
                    ...s.actionBtn,
                    ...(!reservation.isCheckingOut && !reservation.isCompleted ? s.disabledBtn : {}),
                  }}
                  disabled={!reservation.isCheckingOut && !reservation.isCompleted}
                  onClick={() =>
                    navigate(`/cleaningrating/${userId}/${reservation.listingId}/${reservation.id}`)
                  }
                >
                  Review Service
                </button>
              </div>

              {/* FULL WIDTH — Directions */}
              <button
                style={{ ...s.actionBtnFull, ...(disableAccess ? s.disabledBtn : {}) }}
                disabled={disableAccess}
                onClick={() =>
                  !disableAccess &&
                  openGoogleMaps(listing?.address?.latitude, listing?.address?.longitude)
                }
              >
                Directions
              </button>

              {/* FULL WIDTH — Check-In Details */}
              <button
                style={{ ...s.actionBtnFull, ...(disableAccess ? s.disabledBtn : {}) }}
                disabled={disableAccess}
                onClick={() =>
                  !disableAccess &&
                  navigate(`/cleaningchecking/${reservation.listingId}`)
                }
              >
                Service Details
              </button>

              {/* ROW 2 */}
              <div style={s.row2}>
                <button
                  style={s.actionBtn}
                  onClick={() => navigate(`/cleaningreceipt/${userId}/${reservation.id}`)}
                >
                  Get Receipt
                </button>

                <button
                  style={{
                    ...s.actionBtn,
                    ...(reservation.isOngoing ? s.liveCheckIn : s.disabledBtn),
                  }}
                  disabled={!reservation.isOngoing}
                  onClick={() =>
                    reservation.isOngoing &&
                    navigate(`/cleaningcheckin/${reservation.listingId}/${reservation.id}`)
                  }
                >
                  Track Service
                </button>
              </div>
            </div>

            {/* ℹ️ STATUS */}
            <section style={s.card}>
              {renderStatuses(reservation)}
              <div style={s.mutedText}>Current Status</div>
            </section>

            {/* 📕 PAYMENT DETAILS */}
         {/* 📕 PAYMENT DETAILS */}
{listing && (
  <div style={s.bookCard}>
    <div style={s.bookHeader} onClick={() => setOpen((o) => ({ ...o, [reservation.id]: !o[reservation.id] }))}>
      <strong>Payment details</strong>
      {isOpen ? <ExpandLess /> : <ExpandMore />}
    </div>

    <motion.div
      initial={false}
      animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      style={s.whitePage}
    >
      <div style={s.pageTop} />
      <div style={s.pageContent}>

        {/* ── BASE SERVICE ── */}
        <div style={s.sectionHeader}>Your service</div>
        <div style={s.tableRow}>
          <span>{durationLabel}</span>
          <span>€{(unitRate * durationValue).toFixed(2)}</span>
        </div>

        {/* ── EXTRAS ── */}
        {reservation.extras &&
          Object.keys(reservation.extras).length > 0 && (
            <>
              <div style={s.sectionHeader}>Add-on services</div>
              {Object.entries(reservation.extras).map(([name, price]) => (
                <div key={name} style={s.tableRow}>
                  <span>{name}</span>
                  <span>€{Number(price).toFixed(2)}</span>
                </div>
              ))}
              <div style={{ ...s.tableRow, fontWeight: 700 }}>
                <span>Extras total</span>
                <span>
                  €{Object.values(reservation.extras)
                    .reduce((sum, p) => sum + Number(p), 0)
                    .toFixed(2)}
                </span>
              </div>
            </>
          )}

        {/* ── DISCOUNT ── */}
        {reservation.discountedServiceCost < reservation.baseServiceCost && (
          <>
            <div style={s.sectionHeader}>Discount</div>
            <div style={s.tableRow}>
              <span>Base cost</span>
              <span>€{Number(reservation.baseServiceCost).toFixed(2)}</span>
            </div>
            <div style={{ ...s.tableRow, color: G.green, fontWeight: 700 }}>
              <span>Discount applied</span>
              <span>
                -€{(
                  Number(reservation.baseServiceCost) -
                  Number(reservation.discountedServiceCost)
                ).toFixed(2)}
              </span>
            </div>
          </>
        )}

        {/* ── FEES ── */}
        <div style={s.sectionHeader}>Fees</div>
        <div style={s.tableRow}>
          <span>Service fee</span>
          <span>€{serviceFee.toFixed(2)}</span>
        </div>

        {/* ── CREDITS ── */}
        {Number(reservation.creditsUsed) > 0 && (
          <>
            <div style={s.sectionHeader}>Credits</div>
            <div style={{ ...s.tableRow, color: G.green, fontWeight: 700 }}>
              <span>Credits applied</span>
              <span>-€{Number(reservation.creditsUsed).toFixed(2)}</span>
            </div>
          </>
        )}

        {/* ── TOTAL ── */}
        <div style={{
          ...s.tableRow,
          fontWeight: 900,
          fontSize: 15,
          marginTop: 10,
          borderTop: `2px solid ${G.greenBorder}`,
          paddingTop: 10,
        }}>
          <span>Total paid</span>
          <span>€{totalPaid.toFixed(2)}</span>
        </div>

      </div>
    </motion.div>
  </div>
)}

          </aside>
        );
      })}

      {/* empty state */}
      {activeReservations.length === 0 && (
        <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "80px 20px", color: G.muted }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🧹</div>
          <p style={{ fontSize: 18, fontWeight: 700, color: G.textSoft, marginBottom: 8 }}>No active bookings</p>
          <p style={{ fontSize: 14 }}>Your cleaning bookings will appear here.</p>
        </div>
      )}
    </div>
  );
}

/* ── styles ── */
const s: Record<string, React.CSSProperties> = {
  centerWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 24,
    padding: "20px 16px",
    width: "100%",
    maxWidth: 1100,
    margin: "0 auto",
    boxSizing: "border-box",
  },
  stickyCard: {
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${G.greenBorder}`,
    borderRadius: 18,
    padding: 24,
    boxShadow: "0 10px 30px rgba(0,0,0,.08)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    background: G.surface,
  },
  card:      { padding: 16, borderRadius: 16, border: `1px solid ${G.greenBorder}` },
  mutedText: { fontSize: 13, color: G.muted },

  listingCard:      { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, padding: 16, borderRadius: 16, border: `1px solid ${G.greenBorder}`, background: G.surface },
  listingLeft:      { display: "flex", flexDirection: "column", gap: 4, flex: 1 },
  listingTitle:     { fontSize: 15, fontWeight: 900, lineHeight: 1.25, color: G.text },
  listingAddress:   { fontSize: 12, color: G.muted, lineHeight: 1.3 },
  listingImageWrap: { width: 64, height: 64, borderRadius: 12, overflow: "hidden", flexShrink: 0, boxShadow: "0 4px 10px rgba(0,0,0,.1)" },
  listingImage:     { width: "100%", height: "100%", objectFit: "cover" },
  imagePlaceholder: { width: "100%", height: "100%", background: G.greenLight },

  inputCard:  { border: `1px solid ${G.greenBorder}`, borderRadius: 12, overflow: "hidden" },
  grid2:      { display: "grid", gridTemplateColumns: "1fr 1fr" },
  guestRow:   { borderTop: `1px solid ${G.greenBorder}`, padding: "10px 12px" },
  inputCell:  { padding: "12px 14px" },
  inputLabel: { fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: G.green, textTransform: "uppercase" as const },
  inputValue: { fontSize: 13, fontWeight: 600, marginTop: 3, color: G.textSoft },

  actionsCard:   { border: `1px solid ${G.greenBorder}`, borderRadius: 12, overflow: "hidden", background: G.surface },
  row2:          { display: "grid", gridTemplateColumns: "1fr 1fr" },
  actionBtn:     { background: G.surface, color: G.green, fontWeight: 800, fontSize: 12, padding: "14px 10px", cursor: "pointer", border: `1.5px solid ${G.greenBorder2}`, borderBottom: `1px solid ${G.greenBorder}`, transition: "all .2s" },
  actionBtnFull: { width: "100%", background: G.surface, color: G.green, fontWeight: 800, fontSize: 12, padding: "14px 10px", cursor: "pointer", border: `1.5px solid ${G.greenBorder2}`, borderBottom: `1px solid ${G.greenBorder}`, transition: "all .2s" },
  disabledBtn:   { background: "#f3f3f3", color: "#aaa", borderColor: "#ddd", cursor: "not-allowed" },
  liveCheckIn:   { background: G.green, color: "#fff", borderColor: G.greenDark, boxShadow: `0 0 12px rgba(22,163,74,.5)` },

  bookCard:    { border: `2px solid ${G.green}`, borderRadius: 16, padding: 20, boxShadow: `0 6px 20px rgba(22,163,74,.1)` },
  bookHeader:  { display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: G.text, fontSize: 14, fontWeight: 600 },
  whitePage:   { background: G.bg, borderRadius: 10, marginTop: 14, overflow: "hidden" },
  pageTop:     { height: 3, background: `repeating-linear-gradient(90deg,${G.greenBorder} 0 10px,${G.surface} 10px 20px)` },
  pageContent: { padding: "14px 16px" },
  tableRow:    { display: "flex", justifyContent: "space-between", fontSize: 13, color: G.text, padding: "7px 0", borderBottom: `1px dashed ${G.greenBorder}` },
  sectionHeader:{ marginTop: 12, marginBottom: 8, fontWeight: 700, fontSize: 11, color: G.green, letterSpacing: "0.08em", textTransform: "uppercase" as const },
};
